import 'react-calendar/dist/Calendar.css';
import './App.css';
import React, { useEffect, useState } from 'react';

import Calendar from 'react-calendar';
import Cookies from 'js-cookie';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'flag-icons/css/flag-icons.min.css';
import { Checkbox, List, ListItem, Button, TextField } from "@mui/material";
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

const API_URL = process.env.REACT_APP_DAYS_API_URL || window.location.origin;

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

function App() {
    const [andorraCounter, setAndorraCounter] = useState(0);
    const [spainCounter, setSpainCounter] = useState(0);
    const [worldCounter, setWorldCounter] = useState(0);
    const [isAndorraChecked, setIsAndorraChecked] = useState(false);
    const [isSpainChecked, setIsSpainChecked] = useState(false);
    const [isWorldChecked, setIsWorldChecked] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [hasAttachment, setHasAttachment] = useState(false);
    const [note, setNote] = useState("");
    const [dates, setDates] = useState({});
    const [issues, setIssues] = useState([]);
    const [username, setUsername] = useState(Cookies.get('username'));
    const [password, setPassword] = useState(Cookies.get('password'));

    // Helper to get Authorization header
    const getAuthHeader = () => {
        let token;
        const savedUsername = Cookies.get('username');
        const savedPassword = Cookies.get('password');

        if (username && password && (username !== savedUsername || password !== savedPassword)) {
            token = btoa(`${username}:${password}`);
        } else if (savedUsername && savedPassword) {
            token = btoa(`${savedUsername}:${savedPassword}`);
        } else {
            return {};
        }

        return { 'Authorization': `Basic ${token}` };
    };

    // Wrapper for fetch with Authorization header
    const authFetch = async (url, options = {}) => {
        const headers = {
            ...(options.headers || {}),
            ...getAuthHeader(),
        };
        const response = await fetch(url, { ...options, headers });
        if (response.ok) {
            // Update cookies on successful API call
            if (username && password) {
                Cookies.set('username', username);
                Cookies.set('password', password);
            }
        }
        return response;
    };

    useEffect(() => {
        const savedUsername = Cookies.get('username');
        const savedPassword = Cookies.get('password');
        if (savedUsername && savedPassword) {
            setUsername(savedUsername);
            setPassword(savedPassword);
        }
    }, []);

    useEffect(() => {
        if (!username || !password) return;
        authFetch(`${API_URL}/days`)
            .then(res => res.json())
            .then(data => {
                let and = 0, spa = 0, wor = 0;
                let newDates = {};
                data.forEach(row => {
                    newDates[row.day] = {
                        andorra: !!row.andorra,
                        spain: !!row.spain,
                        world: !!row.world,
                        note: row.note || ""
                    };
                    if (row.andorra) and++;
                    if (row.spain) spa++;
                    if (row.world) wor++;
                });
                setDates(newDates);
                setAndorraCounter(and);
                setSpainCounter(spa);
                setWorldCounter(wor);
            });
        // eslint-disable-next-line
    }, [username, password]);

    useEffect(() => {
        if (!username || !password) return;
        fetchIssues();
        // eslint-disable-next-line
    }, [username, password]);

    const onChange = async (date) => {
        setSelectedDate(date);
        const day = date.toLocaleDateString("en-CA");
        checkAttachment(day);
        setIsSpainChecked(false);
        setIsAndorraChecked(false);
        setIsWorldChecked(false);
        setNote("");
        authFetch(`${API_URL}/days/${day}`)
            .then(res => res.json())
            .then(data => {
                if (!data) return;
                if (data.spain) setIsSpainChecked(true);
                if (data.andorra) setIsAndorraChecked(true);
                if (data.world) setIsWorldChecked(true);
                setNote(data.note || "");
                setDates(prev => ({
                    ...prev,
                    [day]: {
                        andorra: !!data.andorra,
                        spain: !!data.spain,
                        world: !!data.world,
                        note: data.note || ""
                    }
                }));
            });
    };

    const saveDay = async (andorra, spain, world, noteValue) => {
        const day = selectedDate.toLocaleDateString("en-CA");
        authFetch(`${API_URL}/days/${day}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify({ andorra, spain, world, note: noteValue })
        }).then(() => {
            setDates(prev => ({
                ...prev,
                [day]: { andorra, spain, world, note: noteValue }
            }));
            fetchIssues();
        });
    };

    const onAndorraChange = async (event) => {
        setIsAndorraChecked(event.target.checked);
        const day = selectedDate.toLocaleDateString("en-CA");
        const andorra = event.target.checked;
        const spain = dates[day]?.spain || false;
        const world = dates[day]?.world || false;
        const noteValue = note;
        saveDay(andorra, spain, world, noteValue);
        setAndorraCounter(prev => andorra ? prev + 1 : prev - 1);
    };

    const onSpainChange = async (event) => {
        setIsSpainChecked(event.target.checked);
        const day = selectedDate.toLocaleDateString("en-CA");
        const spain = event.target.checked;
        const andorra = dates[day]?.andorra || false;
        const world = dates[day]?.world || false;
        const noteValue = note;
        saveDay(andorra, spain, world, noteValue);
        setSpainCounter(prev => spain ? prev + 1 : prev - 1);
    };

    const onWorldChange = async (event) => {
        setIsWorldChecked(event.target.checked);
        const day = selectedDate.toLocaleDateString("en-CA");
        const world = event.target.checked;
        const andorra = dates[day]?.andorra || false;
        const spain = dates[day]?.spain || false;
        const noteValue = note;
        saveDay(andorra, spain, world, noteValue);
        setWorldCounter(prev => world ? prev + 1 : prev - 1);
    };

    const onNoteChange = async (event) => {
        const newNote = event.target.value;
        setNote(newNote);
        const day = selectedDate.toLocaleDateString("en-CA");
        const andorra = dates[day]?.andorra || false;
        const spain = dates[day]?.spain || false;
        const world = dates[day]?.world || false;
        saveDay(andorra, spain, world, newNote);
    };

    const tileClassName = ({ date, view }) => {
        const day = date.toLocaleDateString("en-CA");
        if (view !== 'month') {
            return '';
        }
        if (dates[day] === undefined) {
            return 'grey';
        }
        if (dates[day].spain && dates[day].andorra) {
            return 'yellow';
        }
        if (dates[day].spain) {
            return 'red';
        }
        if (dates[day].andorra) {
            return 'green';
        }
        if (dates[day].world) {
            return 'orange';
        }
        // default
        return 'grey';
    };

    const daysLeft = () => {
        let now = new Date();
        let end = new Date(now.getFullYear() + 1, 0, 1);
        let diff = end.getTime() - now.getTime();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };

    const checkAttachment = async (day) => {
        authFetch(`${API_URL}/attachment/${day}`, { method: 'HEAD' })
            .then(res => setHasAttachment(res.ok))
            .catch(() => setHasAttachment(false));
    };

    const downloadAttachment = async () => {
        const day = selectedDate.toLocaleDateString("en-CA");
        window.open(`${API_URL}/attachment/${day}`, '_blank');
    };

    const uploadAttachment = async (event) => {
        if (!event.target.files) {
            console.error("no file selected!");
            return;
        }
        const file = event.target.files[0];
        const day = selectedDate.toLocaleDateString("en-CA");
        const formData = new FormData();
        formData.append('file', file);
        authFetch(`${API_URL}/attachment/${day}`, {
            method: 'POST',
            body: formData
        }).then(() => setHasAttachment(true));
    };

    const fetchIssues = () => {
        authFetch(`${API_URL}/issues`)
            .then(res => res.json())
            .then(setIssues);
    };

    return (
        <div className="App">
            <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', gap: 8, justifyContent: 'center' }}>
                <TextField
                    label="User"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    size="small"
                    variant="outlined"
                    autoComplete="username"
                />
                <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    size="small"
                    variant="outlined"
                    autoComplete="current-password"
                />
            </div>
            <Calendar
                defaultView='month'
                locale='es-ES'
                showDoubleView={false}
                showFixedNumberOfWeeks={false}
                showNeighboringMonth={false}
                showWeekNumbers={false}
                minDate={new Date(2025, 0, 1)}
                maxDate={new Date(2025, 11, 31)}
                onChange={onChange}
                tileClassName={tileClassName}
                value={selectedDate}
            />
            <div className="checkboxes">
                <div style={{ display: 'block', justifyContent: 'center' }}>
                    <ListItem alignItems="center" style={{ width: 'auto' }}>
                        <Checkbox
                            id="andorraCheck"
                            checked={isAndorraChecked}
                            onChange={onAndorraChange}
                        />
                        <span className="fi fi-ad"></span><span>Andorra</span>
                    </ListItem>
                    <ListItem alignItems="center" style={{ width: 'auto' }}>
                        <Checkbox
                            id="spainCheck"
                            checked={isSpainChecked}
                            onChange={onSpainChange}
                        />
                        <span className="fi fi-es"></span><span>España</span>
                    </ListItem>
                    <ListItem alignItems="center" style={{ width: 'auto' }}>
                        <Checkbox
                            id="worldCheck"
                            checked={isWorldChecked}
                            onChange={onWorldChange}
                        />
                        <span className="fi fi-eu"></span><span>World</span>
                    </ListItem>
                </div>
            </div>
            <div className="extra">
                <List>
                    <ListItem alignItems="center">
                        <TextField
                            label="Nota"
                            value={note}
                            onChange={onNoteChange}
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                    </ListItem>
                    <ListItem alignItems="center">
                        <div style={{ display: 'none', gap: 8 }}>
                            <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                tabIndex={-1}
                                startIcon={<CloudUploadIcon />}
                            >
                                Adjunta justificante
                                <VisuallyHiddenInput type="file" onChange={uploadAttachment} />
                            </Button>
                            <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                tabIndex={-1}
                                startIcon={<CloudDownloadIcon />}
                                disabled={!hasAttachment}
                                onClick={downloadAttachment}
                            >
                                Descarga justificante
                            </Button>
                        </div>
                    </ListItem>
                </List>
            </div>
            <div className="counter">
                <div><b>Andorra: {andorraCounter}</b> (faltan {183 - andorraCounter})</div>
                <div><b>España: {spainCounter}</b> (faltan {183 - spainCounter})</div>
                <div><b>World: {worldCounter}</b></div>
                <br />
                <div>Quedan: {daysLeft()}&nbsp;&nbsp;
                    {issues.length === 0 ? (
                        <span style={{ color: 'green', verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center' }}>
                            <CheckCircleIcon style={{ verticalAlign: 'middle' }} />&nbsp;
                        </span>
                    ) : (
                        <span style={{ color: '#fbc02d', verticalAlign: 'middle', display: 'inline-flex', alignItems: 'center' }}>
                        <WarningIcon style={{ verticalAlign: 'middle' }} />&nbsp;
                            <a
                                href={`${API_URL}/issues`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#fbc02d', textDecoration: 'underline', fontWeight: 'bold' }}
                            >
                        {issues.length} problema(s) detectado(s)
                        </a>
                    </span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default App;